import { Injectable, NgZone } from "@angular/core";
import { Router } from "@angular/router";
import { AngularFireMessaging } from "@angular/fire/compat/messaging";

// Example payload interface. Adjust keys/types to match your actual data.
interface MessageData {
  [key: string]: any; // fallback
}

interface MessagePayload {
  data: MessageData;
  [key: string]: any; // fallback
}

@Injectable({
  providedIn: "root",
})
export class MessagingService {
  constructor(
    private angularFireMessaging: AngularFireMessaging,
    private router: Router,
    private ngZone: NgZone
  ) {
    console.log("MessagingService initialized");

    // Subscribe to FCM foreground messages
    // this.angularFireMessaging.messages.subscribe((npayload) => {
    //   console.log(
    //     "[firebase-messaging-foreground] Received message:",
    //     npayload
    //   );

    //   // Iterate over all keys in npayload and print their values
    //   Object.entries(npayload).forEach(([key, value]) => {
    //     console.log(`Key: ${key} => Value:`, value);
    //   });

    //   const payload = npayload as MessagePayload;

    //   console.log("Payload Data:", payload.data);
    //   // Iterate over all keys in npayload and print their values
    //   Object.entries(payload.data).forEach(([key, value]) => {
    //     console.log(`Key: ${key} => Value:`, value);
    //   });

    //   // only foward if data has a type, ie is a message, otherwise it might be used for other things
    //   if (payload.data && payload.data.type) {
    //     console.log(
    //       "Sending payload data as its own event:",
    //       payload.data.type
    //     );

    //     window.dispatchEvent(
    //       new CustomEvent(payload.data.type, {
    //         detail: {
    //           type: payload.data.type,
    //           payload: payload,
    //         },
    //       })
    //     );
    //   }
    // });

    if ("serviceWorker" in navigator && navigator.serviceWorker) {
      // this is for pwa service worker support
      navigator.serviceWorker.addEventListener(
        "message",
        (event: MessageEvent) => {
          const envelope: any = (event && event.data) || {};
          console.log("=== Message from service worker ===");
          console.log("Full envelope:", envelope);
          console.log("envelope.type:", envelope.type);
          console.log("envelope.payload:", envelope.payload);
          console.log("envelope.data:", envelope.data);

          const type: string = envelope.type || "FORWARD_DATA";
          const payload =
            typeof envelope.payload !== "undefined"
              ? envelope.payload
              : envelope.data;

          console.log("Resolved type:", type);
          console.log("Resolved payload:", payload);

          // Handle push notifications directly in messaging service
          if (type === "push") {
            console.log("Type is 'push'");

            // Check if service worker already showed the notification
            // This prevents duplicate notifications and sounds in Chrome
            if (payload && payload._fromServiceWorkerNotification) {
              console.log(
                "Service worker already showed notification - skipping duplicate"
              );
              // Still forward data events, but don't show notification or play sound again
              if (payload.data && payload.data.type) {
                console.log("Forwarding data event:", payload.data.type);
                window.dispatchEvent(
                  new CustomEvent(payload.data.type, {
                    detail: {
                      type: payload.data.type,
                      payload: payload,
                    },
                  })
                );
              }
              return;
            }

            console.log("Calling showNotification");
            this.showNotification(payload);
            return;
          }

          console.log("Dispatching custom event with type:", type);
          window.dispatchEvent(
            new CustomEvent(type, {
              detail: {
                type,
                payload,
              },
            })
          );
        }
      );
    }

    // Listen for PLAY_SOUND events from service worker
    window.addEventListener("PLAY_SOUND", (event: Event) => {
      console.log("[SOUND] PLAY_SOUND event received:", event);
      const soundUrl = (event as CustomEvent<MessagePayload>).detail.payload;
      console.log("[SOUND] Sound URL from event:", soundUrl);
      this.playSound(soundUrl);
    });

    // React to notification clicks forwarded by the Service Worker
    window.addEventListener("NOTIFICATION_CLICK", (event: Event) => {
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
      console.log("🔔 [PUSH-NAV] NOTIFICATION_CLICK event received");
      console.log("🔔 [PUSH-NAV] Event:", event);

      try {
        const ce = event as CustomEvent<any>;
        const detail = ce.detail || {};
        console.log("🔔 [PUSH-NAV] Event detail:", detail);

        const clickPayload = detail.payload;
        console.log("🔔 [PUSH-NAV] Click payload:", clickPayload);

        const notification = clickPayload.notification;
        console.log("🔔 [PUSH-NAV] Notification:", notification);

        const data = notification.data;
        console.log("🔔 [PUSH-NAV] Notification data:", data);

        const candidateUrl = data.url;
        console.log("🔔 [PUSH-NAV] Candidate URL:", candidateUrl);

        // Resolve against current origin to get an absolute URL
        const targetUrl = new URL(candidateUrl, window.location.origin);
        console.log("🔔 [PUSH-NAV] Resolved target URL:", targetUrl.href);
        console.log("🔔 [PUSH-NAV] Target origin:", targetUrl.origin);
        console.log("🔔 [PUSH-NAV] Current origin:", window.location.origin);

        // Prefer same-origin in-place navigation using Angular Router
        if (targetUrl.origin === window.location.origin) {
          console.log("🔔 [PUSH-NAV] ✅ Same origin - using Angular Router");

          try {
            window.focus?.();
          } catch {}

          // ✅ Use Angular Router with NgZone to prevent webview reload
          // Extract the path and query params from the URL
          const path = targetUrl.pathname + targetUrl.search + targetUrl.hash;
          console.log("🔔 [PUSH-NAV] Extracted path:", path);
          console.log("🔔 [PUSH-NAV] Current router URL:", this.router.url);
          console.log(
            "🔔 [PUSH-NAV] Calling router.navigateByUrl inside NgZone..."
          );

          this.ngZone.run(() => {
            console.log(
              "🔔 [PUSH-NAV] Inside NgZone.run - navigating to:",
              path
            );
            this.router.navigateByUrl(path).then(
              (success) => {
                console.log(
                  "🔔 [PUSH-NAV] ✅ Navigation completed. Success:",
                  success
                );
                console.log("🔔 [PUSH-NAV] New router URL:", this.router.url);
              },
              (error) => {
                console.error("🔔 [PUSH-NAV] ❌ Navigation failed:", error);
              }
            );
          });
        } else {
          console.log("🔔 [PUSH-NAV] ⚠️ Cross-origin - opening externally");
          window.open(targetUrl.href, "_self");
        }
      } catch (err) {
        console.error(
          "🔔 [PUSH-NAV] ❌ Error handling notification click:",
          err
        );
        console.error("🔔 [PUSH-NAV] Event that caused error:", event);
      }
      console.log("━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━");
    });

    window.addEventListener("FORWARD_DATA", (event: Event) => {
      const customEvent = event as CustomEvent<MessagePayload>;
      console.log(
        "[PREARRIVAL-PUSH] Angular MessagingService received FORWARD_DATA event:",
        customEvent.detail
      );

      // only foward if there is a message type
      if (customEvent.detail.data && customEvent.detail.data.type) {
        const dispatchDetail = {
          type: customEvent.detail.data.type,
          payload: customEvent.detail,
        };
        console.log(
          "[PREARRIVAL-PUSH] Redispatching as specific event type:",
          customEvent.detail.data.type,
          dispatchDetail
        );
        window.dispatchEvent(
          new CustomEvent(customEvent.detail.data.type, {
            detail: dispatchDetail,
          })
        );
        console.log(
          "[PREARRIVAL-PUSH] Redispatch complete for type:",
          customEvent.detail.data.type
        );
      } else {
        console.log(
          "[PREARRIVAL-PUSH] No data.type in detail; not redispatching."
        );
      }
    });
  }

  /**
   * Show a notification using the Notification API
   */
  private async showNotification(payload: any) {
    console.log("=== showNotification called ===");
    console.log("Full payload:", payload);
    console.log("payload.data:", payload?.data);
    console.log("payload.notification:", payload?.notification);

    // Normalize title/body/icon from FCM payload
    const title =
      payload?.notification?.title || payload?.data?.title || "Notification";
    const body = payload?.notification?.body || payload?.data?.body || "";
    const icon =
      payload?.notification?.icon || payload?.data?.icon || "/favicon.ico";

    console.log("Normalized notification data:");
    console.log("  title:", title);
    console.log("  body:", body);
    console.log("  icon:", icon);

    // Check if the browser supports notifications
    if (!("Notification" in window)) {
      console.warn("This browser does not support notifications");
      return;
    }

    console.log("Notification.permission:", Notification.permission);

    // Forward data messages for in-app handlers
    if (payload?.data && payload.data.type) {
      console.log("Forwarding data message as event:", payload.data.type);
      window.dispatchEvent(
        new CustomEvent(payload.data.type, {
          detail: {
            type: payload.data.type,
            payload: payload,
          },
        })
      );
    }

    // Check notification permission
    if (Notification.permission === "granted") {
      console.log("Creating notification with title:", title);

      // Try to use service worker registration if available
      if ("serviceWorker" in navigator) {
        try {
          const registration = await navigator.serviceWorker.ready;
          console.log("Service worker registration:", registration);

          // Close old notifications to prevent buildup (Firefox issue)
          const oldNotifications = await registration.getNotifications();
          console.log("Closing old notifications:", oldNotifications.length);
          oldNotifications.forEach((n) => n.close());

          const notificationPromise = registration.showNotification(title, {
            body,
            icon,
            badge: icon,
            data: payload?.data,
            requireInteraction: true, // Changed to true for Firefox - keeps notification visible
            silent: false,
            tag: "medicnow-primary",
          });
          await notificationPromise;
          console.log(
            "Notification shown via service worker - promise resolved"
          );

          // Verify notification was created
          const notifications = await registration.getNotifications();
          console.log(
            "Active notifications:",
            notifications.length,
            notifications
          );
        } catch (error) {
          console.error(
            "Error showing notification via service worker:",
            error
          );
          // Fallback to direct notification
          const notification = new Notification(title, {
            body,
            icon,
            data: payload?.data,
            tag: `notification-${Date.now()}`,
          });
          console.log("Notification created directly:", notification);
        }
      } else {
        // No service worker, use direct notification
        const notification = new Notification(title, {
          body,
          icon,
          data: payload?.data,
          tag: `notification-${Date.now()}`,
        });
        console.log("Notification created directly:", notification);
      }

      // Play sound if specified
      if (payload?.data?.sound) {
        console.log(
          "[SOUND] Sound specified in payload.data.sound:",
          payload.data.sound
        );
        console.log("[SOUND] Calling playSound from showNotification");
        this.playSound(payload.data.sound);
      } else {
        console.log("[SOUND] No sound specified in payload.data");
      }
    } else if (Notification.permission !== "denied") {
      console.log("Requesting notification permission...");
      // Request permission if not denied
      Notification.requestPermission().then(async (permission) => {
        console.log("Permission result:", permission);
        if (permission === "granted") {
          // Try service worker first
          if ("serviceWorker" in navigator) {
            try {
              const registration = await navigator.serviceWorker.ready;
              await registration.showNotification(title, {
                body,
                icon,
                badge: icon,
                data: payload?.data,
                requireInteraction: false,
                silent: false,
                tag: `notification-${Date.now()}`,
              });
              console.log("Notification shown via service worker");
            } catch (error) {
              console.error("Error showing notification:", error);
            }
          } else {
            const notification = new Notification(title, {
              body,
              icon,
              data: payload?.data,
              tag: `notification-${Date.now()}`,
            });
            console.log("Notification created:", notification);
          }
        }
      });
    } else {
      console.warn("Notification permission is denied");
    }
  }

  /**
   * Play a sound from the provided URL (in this case, from your S3 bucket).
   */
  private playSound(soundUrl: string) {
    console.log("[SOUND] ========== playSound() called ==========");
    console.log("[SOUND] Sound URL parameter:", soundUrl);

    const fullUrl = `https://sandbox-medicnow-app.s3.us-east-1.amazonaws.com/sounds/${soundUrl}`;
    console.log("[SOUND] Full audio URL:", fullUrl);

    try {
      const audio = new Audio(fullUrl);
      console.log("[SOUND] Audio element created:", audio);
      console.log("[SOUND] Audio readyState:", audio.readyState);
      console.log("[SOUND] Attempting to play audio...");

      const playPromise = audio.play();

      playPromise
        .then(() => {
          console.log("[SOUND] ✓ Audio playback started successfully");
          console.log("[SOUND] Audio duration:", audio.duration);
          console.log("[SOUND] Audio volume:", audio.volume);
        })
        .catch((error) => {
          console.error("[SOUND] ✗ Error playing audio:", error);
          console.error("[SOUND] Error name:", error.name);
          console.error("[SOUND] Error message:", error.message);
          console.error(
            "[SOUND] This is likely due to browser autoplay policy"
          );
        });
    } catch (error) {
      console.error("[SOUND] ✗ Error creating Audio element:", error);
    }

    console.log("[SOUND] ========== playSound() completed ==========");
  }
}
